import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostBinding, Input, NgZone, OnChanges, OnInit, signal, SimpleChanges, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { SafeResourceUrl } from "@angular/platform-browser";
import { EmbeddedDashboard } from "@superset-ui/embedded-sdk";
import { catchError, first, firstValueFrom, of, Subscription, tap, timer } from "rxjs";
import { Animations } from "src/app/animations/animations";
import { FeatureFlags } from "src/app/api/models/profile/profile-feature-flags.enum";
import { FeatureFlagQuery } from "src/app/feature-flag/state/feature-flag.query";
import { LoggerService } from "src/app/logger/logger.service";
import { ContentPipeModule } from "src/app/services/content/content-pipe.module";
import { DashboardService } from "../dashboard.service";
import { LookerDashboardTypeEnum } from "../models/dashboard.enums";
import { DashboardEmbed } from "../models/dashboard-embed";
import { SupersetEmbedder } from "../superset-embedder.service";
import { ActivatedRoute } from "@angular/router";
import { LookerDashboardRouteToTitlePipe } from "./looker-dashboard-route-to-title.pipe";

@Component({
    selector: 'looker-dashboard',
    templateUrl: './looker-dashboard.component.html',
    styleUrl: './looker-dashboard.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        ...Animations.fadeInUpAll
    ],
    imports: [
        CommonModule,
        ContentPipeModule,
    ],
    providers: [
        LookerDashboardRouteToTitlePipe
    ]
})
export class LookerDashboardComponent implements OnInit, OnChanges {
  @Input() type: LookerDashboardTypeEnum;
  @Input() dashboardUuid?: string;
  @ViewChild('supersetMount', { static: true }) private supersetMount: ElementRef<HTMLElement>;

  /** Signed Looker SSO URLs are short-lived; refresh before expiry within a session. */
  private static readonly SSO_REFRESH_MS = 25 * 60 * 1000;
  private static readonly SUPERSET_SIZE_POLL_MS = 1000;
  private readonly loggerName = 'LookerDashboardComponent';

  url = signal<SafeResourceUrl | null>(null);
  engine = signal<'looker' | 'superset'>('looker');
  supersetHeight = signal<number | null>(null);
  loading = signal<boolean>(true);
  error = signal<boolean>(false);
  title: string;

  private resolvedType: LookerDashboardTypeEnum;
  private resolvedUuid?: string;
  private initialized = false;
  private ssoRefreshSub: Subscription | null = null;
  private sizePollSub: Subscription | null = null;
  private embedded: EmbeddedDashboard | null = null;
  /** Bumped on every (re)load and teardown so late async results from a stale embed are discarded. */
  private generation = 0;

  constructor(
    private dashboardService: DashboardService,
    private supersetEmbedder: SupersetEmbedder,
    private featureFlagQuery: FeatureFlagQuery,
    private route: ActivatedRoute,
    private destroyRef: DestroyRef,
    private ngZone: NgZone,
    private loggerService: LoggerService,
    private lookerDashboardRouteToTitlePipe: LookerDashboardRouteToTitlePipe
  ) {
    this.destroyRef.onDestroy(() => {
      this.generation++;
      this.teardownSuperset();
      this.stopSsoRefresh();
    });
  }

  @HostBinding('@fadeInUpAllAnimation')
  get hostAnimation() {
    return true;
  }

  @HostBinding('class')
  get hostClass(): string {
    return this.type ? '' : 'page-padding u-display-block no-bottom-padding';
  }

  ngOnInit(): void {
    this.resolveInputs();
    this.initialized = true;
    // Arm the Looker timer first; loadSupersetByUuid stops it synchronously when Superset is chosen.
    this.scheduleSsoRefresh();
    this.loadDashboard();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized || !(changes['type'] || changes['dashboardUuid'])) {
      return;
    }
    const previousType = this.resolvedType;
    const previousUuid = this.resolvedUuid;
    this.resolveInputs();
    if (previousType !== this.resolvedType || previousUuid !== this.resolvedUuid) {
      // A Superset mount torn down by this reload stops the Looker timer; re-arm it for the Looker path.
      this.scheduleSsoRefresh();
      this.loadDashboard();
    }
  }

  private resolveInputs(): void {
    // Type and uuid come from the same source: a bound `type` input means the host owns both.
    const data = this.route?.snapshot?.data;
    this.resolvedType = (this.type || data?.['type']) as LookerDashboardTypeEnum;
    this.resolvedUuid = this.type ? this.dashboardUuid : data?.['dashboardUuid'];
    this.title = this.lookerDashboardRouteToTitlePipe.transform(this.resolvedType);
  }

  onIframeLoad(): void {
    setTimeout(() => this.loading.set(false), 1500);
  }

  retry(): void {
    this.loadDashboard();
  }

  private loadDashboard(): void {
    const generation = ++this.generation;
    this.teardownSuperset();
    this.loading.set(true);
    this.error.set(false);
    if (this.resolvedUuid && this.featureFlagQuery.getFlag(FeatureFlags.SupersetDashboards)) {
      this.loadSupersetByUuid(this.resolvedUuid, generation);
      return;
    }
    this.dashboardService.getEmbed(this.resolvedType).pipe(
      first(),
      tap(embed => {
        if (generation === this.generation) {
          this.render(embed);
        }
      }),
      catchError(() => {
        if (generation === this.generation) {
          this.showError();
        }
        return of(null);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  private loadSupersetByUuid(uuid: string, generation: number): void {
    // The SDK refreshes the 300 s guest token itself, so no 25-min Looker timer.
    this.stopSsoRefresh();
    this.url.set(null);
    this.engine.set('superset');
    this.dashboardService.getSupersetToken(uuid).pipe(
      first(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: response => {
        if (generation !== this.generation) {
          return;
        }
        this.mountSuperset({
          embedUuid: response.embedUuid,
          supersetDomain: response.supersetDomain,
          guestToken: response.token,
          // Not cancelled on destroy: one small POST; fetchGuestToken's generation check drops a stale result.
          refreshToken: () => firstValueFrom(this.dashboardService.getSupersetToken(uuid)).then(r => r.token),
        }, generation);
      },
      error: err => {
        if (generation !== this.generation) {
          return;
        }
        this.loggerService.error(this.loggerName, `Superset token request failed for ${uuid}: ${err?.status ?? ''} ${err?.message ?? err}`);
        this.showError();
      },
    });
  }

  private render(embed: DashboardEmbed): void {
    this.engine.set('looker');
    this.url.set(embed.url);
    this.scheduleSsoRefresh();
  }

  private async mountSuperset(
    embed: { embedUuid: string; supersetDomain: string; guestToken: string; refreshToken: () => Promise<string> },
    generation: number
  ): Promise<void> {
    // The SDK calls fetchGuestToken() right at mount: serve the backend-issued token for that first call
    // (no request), then re-POST for every refresh.
    let initialToken: string | null = embed.guestToken;
    try {
      const embedded = await this.supersetEmbedder.embed({
        id: embed.embedUuid,
        supersetDomain: embed.supersetDomain,
        mountPoint: this.supersetMount.nativeElement,
        fetchGuestToken: () => {
          // First call skips the generation guard on purpose: the token is already in hand, and a
          // superseded mount is unmounted by the generation check after embed() resolves.
          if (initialToken) {
            const token = initialToken;
            initialToken = null;
            return Promise.resolve(token);
          }
          return this.fetchGuestToken(generation, embed.refreshToken);
        },
        dashboardUiConfig: {
          hideTitle: true,
          // Each chart's menu: CSV/XLSX/image download and drill. Superset scopes exports to the token's tenant.
          // Superset scopes exports to the token's RLS clause — tenant isolation enforced server-side.
          hideChartControls: false,
          filters: { visible: true, expanded: true },
        },
      });

      if (generation !== this.generation || this.error()) {
        embedded.unmount();
        return;
      }
      this.embedded = embedded;
      this.loading.set(false);
      this.startSizePoll(embedded);
    } catch (err) {
      if (generation === this.generation) {
        this.loggerService.error(this.loggerName, `Superset embed failed for ${embed.embedUuid}: ${err}`);
        this.showError();
      }
    }
  }

  private async fetchGuestToken(generation: number, refreshToken: () => Promise<string>): Promise<string> {
    try {
      return await refreshToken();
    } catch (err) {
      if (generation === this.generation) {
        this.loggerService.error(this.loggerName, `Superset guest token refresh failed: ${(err as { message?: string })?.message ?? err}`);
        this.showError();
      }
      throw err;
    }
  }

  private startSizePoll(embedded: EmbeddedDashboard): void {
    // Outside the zone: a 1 s poll must not trigger app-wide change detection; the signal set still marks the view.
    this.sizePollSub = this.ngZone.runOutsideAngular(() => timer(
      LookerDashboardComponent.SUPERSET_SIZE_POLL_MS,
      LookerDashboardComponent.SUPERSET_SIZE_POLL_MS
    ).pipe(
      tap(() => {
        embedded.getScrollSize()
          .then(size => {
            if (this.embedded === embedded && size?.height > 0) {
              this.supersetHeight.set(size.height);
            }
          })
          .catch(() => { /* the iframe may be mid-navigation; the next tick retries */ });
      }),
    ).subscribe());
  }

  private teardownSuperset(): void {
    this.sizePollSub?.unsubscribe();
    this.sizePollSub = null;
    this.embedded?.unmount();
    this.embedded = null;
    this.supersetHeight.set(null);
  }

  private showError(): void {
    this.teardownSuperset();
    this.error.set(true);
    this.loading.set(false);
  }

  private scheduleSsoRefresh(): void {
    if (this.ssoRefreshSub) {
      return;
    }
    this.ssoRefreshSub = timer(LookerDashboardComponent.SSO_REFRESH_MS, LookerDashboardComponent.SSO_REFRESH_MS).pipe(
      tap(() => this.loadDashboard()),
    ).subscribe();
  }

  private stopSsoRefresh(): void {
    this.ssoRefreshSub?.unsubscribe();
    this.ssoRefreshSub = null;
  }
}
